import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiSearch } from '../api.js';
import Footer from '../components/Footer.jsx';
import { Search, BookOpen, ShoppingBag } from '../icons.jsx';

// 多关键词：前端把空格分隔的词用 + 连接（后端按 + 拆分做 AND 匹配）
function toQuery(input) {
  return input.trim().split(/\s+/).filter(Boolean).join('+');
}

function QrCard({ title, img, alt, caption }) {
  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center hover:shadow-md transition-shadow">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="w-48 h-48 md:w-56 md:h-56 bg-gray-100 rounded-lg overflow-hidden">
        <img alt={alt} src={img} className="w-full h-full object-contain" />
      </div>
      <p className="mt-4 text-sm text-gray-500 text-center">{caption}</p>
    </div>
  );
}

function Card({ r }) {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 border border-gray-100">
      <div className="bg-gray-200 h-64 overflow-hidden relative group">
        {r.ImageUrl ? (
          <img alt={r.Name || r.Code || ''} src={r.ImageUrl} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
        ) : null}
        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">{r.Code}</div>
      </div>
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-900 mb-2 truncate flex items-center gap-2" title={r.Name}>
          <span className="bg-orange-600 text-white text-xs px-2 py-1 rounded font-mono">{r.Code}</span>
          <span className="truncate">{r.Name}</span>
        </h3>
        <div className="flex flex-col gap-3 mt-4">
          {r.TutorialUrl && (
            <a href={r.TutorialUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors">
              <BookOpen w={16} h={16} />查看教程
            </a>
          )}
          {r.ProductUrl && (
            <a href={r.ProductUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-2 bg-orange-50 text-orange-700 hover:bg-orange-100 rounded-lg text-sm font-medium transition-colors">
              <ShoppingBag w={16} h={16} />购买商品
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const doSearch = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await apiSearch(toQuery(input));
      setResults(Array.isArray(data) ? data : []);
      setSearched(true);
    } catch {
      setError('搜索失败，请稍后重试或检查网络连接');
      setResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const showResults = searched && results.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-gray-800 font-sans">
      <main className="flex-grow container mx-auto px-4 py-8 md:py-16 flex flex-col items-center">
        {/* Logo */}
        <div className="mb-12 md:mb-16 w-full max-w-md flex justify-center">
          <img
            alt="衣图云"
            src="/logo.png"
            onClick={() => { setInput(''); setResults([]); setSearched(false); navigate('/'); }}
            className="h-20 md:h-28 object-contain cursor-pointer hover:opacity-90 transition-opacity"
          />
        </div>

        {/* Search */}
        <div className="w-full max-w-2xl mb-12">
          <form onSubmit={doSearch} className="relative flex items-center">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入款号查找"
              type="text"
              className="w-full px-6 py-4 pl-12 rounded-full border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg transition-all"
            />
            <Search w={24} h={24} className="lucide-search absolute left-4 text-gray-400 w-6 h-6" />
            <button type="submit" disabled={loading} className="absolute right-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full font-medium transition-colors disabled:opacity-60">
              {loading ? '搜索中' : '搜索'}
            </button>
          </form>
          {error && <p className="mt-3 text-center text-sm text-red-600">{error}</p>}
        </div>

        {/* Results OR QR cards */}
        {showResults ? (
          <div className="w-full max-w-5xl mb-16 animate-fade-in">
            <h2 className="text-xl font-semibold mb-6 text-gray-700 border-l-4 border-blue-600 pl-3">搜索结果 ({results.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((r) => <Card key={r.Id} r={r} />)}
            </div>
          </div>
        ) : searched && results.length === 0 ? (
          <div className="w-full max-w-5xl mb-16 text-center text-gray-400 py-12">未找到相关结果，请尝试其他关键词</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 w-full max-w-4xl mt-auto mb-8">
            <QrCard title="扫码进群交流" img="/joinus.png" alt="进群二维码" caption="获取更多教程资讯" />
            <QrCard title="快团团扫码购" img="/ktt.png" alt="快团团二维码" caption="超值好物团购" />
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
